/**
 * Extracts the first frame of a video file as a Blob (JPEG).
 */
export const extractThumbnailFromVideo = (file: File): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url = URL.createObjectURL(file);
    
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;
    video.src = url;

    const cleanup = () => {
      video.onloadedmetadata = null;
      video.onseeked = null;
      video.onerror = null;
      URL.revokeObjectURL(url);
    };

    video.onloadedmetadata = () => {
      video.currentTime = 0.1; // Seek to 0.1s to avoid initial black frame
    };

    video.onseeked = () => {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d');
      ctx?.drawImage(video, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          cleanup();
          if (blob) {
            resolve(blob);
          } else {
            reject(new Error('Failed to capture thumbnail'));
          }
        },
        'image/jpeg',
        0.85,
      );
    };

    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video for thumbnail extraction'));
    };
  });
};

/**
 * Safari requires video/mp4 for smooth playback of MediaRecorder outputs.
 */
const getSupportedMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';

  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  const candidates = isSafari
    ? ['video/mp4', 'video/webm;codecs=vp9', 'video/webm']
    : ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm', 'video/mp4'];

  for (const type of candidates) {
    if (MediaRecorder.isTypeSupported?.(type)) {
      return type;
    }
  }

  return '';
};

/**
 * Extracts a motion thumbnail (silent video) cross-browser (including Safari).
 */
export const extractMotionThumbnailFromVideo = (
  file: File,
  maxDuration = 5
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const objectUrl = URL.createObjectURL(file);
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.src = objectUrl;

    video.style.position = 'fixed';
    video.style.width = '1px';
    video.style.height = '1px';
    video.style.opacity = '0';
    video.style.pointerEvents = 'none';
    document.body.appendChild(video);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    let rafId: number | null = null;
    let started = false;
    let canvasStream: MediaStream | null = null;
    let timeoutId: NodeJS.Timeout | null = null;

    const cleanup = () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (timeoutId !== null) clearTimeout(timeoutId);
      if (canvasStream) {
        canvasStream.getTracks().forEach((track) => track.stop());
      }
      URL.revokeObjectURL(objectUrl);
      video.remove();
    };

    const beginCapture = () => {
      if (started) return;
      started = true;

      try {
        if (!ctx) {
          cleanup();
          reject(new Error('Canvas 2D context unavailable'));
          return;
        }

        canvas.width = video.videoWidth || 720;
        canvas.height = video.videoHeight || 1280;

        const duration = Math.min(video.duration || maxDuration, maxDuration);

        const drawFrame = () => {
          if (video.paused || video.ended) return;
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          rafId = requestAnimationFrame(drawFrame);
        };
        drawFrame();

        canvasStream = (canvas as HTMLCanvasElement & { captureStream?: (fps?: number) => MediaStream }).captureStream?.(30) || null;

        if (!canvasStream) {
          cleanup();
          reject(new Error('Browser does not support canvas stream capture'));
          return;
        }

        const mimeType = getSupportedMimeType();
        const mediaRecorder = new MediaRecorder(canvasStream, mimeType ? { mimeType } : undefined);
        const outputType = mimeType.split(';')[0] || 'video/mp4';

        const chunks: Blob[] = [];
        mediaRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };

        mediaRecorder.onstop = () => {
          const motionBlob = new Blob(chunks, { type: outputType });
          cleanup();
          if (motionBlob.size === 0) {
            reject(new Error('Motion thumbnail capture produced an empty file'));
            return;
          }
          resolve(motionBlob);
        };

        mediaRecorder.onerror = (e) => {
          cleanup();
          reject(e instanceof Error ? e : new Error('MediaRecorder failed'));
        };

        mediaRecorder.start(100);

        const stopRecording = () => {
          if (mediaRecorder.state !== 'inactive') {
            video.pause();
            mediaRecorder.stop();
          }
        };

        video.onended = stopRecording;
        timeoutId = setTimeout(stopRecording, duration * 1000 + 200);
      } catch (err) {
        cleanup();
        reject(err instanceof Error ? err : new Error('Failed to start motion thumbnail recording'));
      }
    };

    video.onloadedmetadata = () => {
      video.play().catch((err) => {
        cleanup();
        reject(err);
      });
    };

    video.onplaying = beginCapture;

    video.onerror = () => {
      cleanup();
      reject(new Error('Failed to load video for motion thumbnail extraction'));
    };
  });
};
